type AnyObj = Record<string, unknown>;

const isPlainObject = (val: unknown): val is AnyObj =>
  typeof val === "object" && val !== null && !Array.isArray(val);

const isDateValue = (val: unknown): boolean => {
  if (val instanceof Date) return true;
  if (typeof val === "string") {
    const d = new Date(val);
    return !isNaN(d.getTime());
  }
  return false;
};

const isEmptyValue = (val: unknown): boolean => {
  if (isDateValue(val)) return false;
  return val === "" || val === null || val === undefined;
};

const cleanArray = (arr: unknown[]): unknown => {
  const cleaned = arr.map((item) => cleanValue(item)).filter((item) => !isEmptyValue(item));
  return cleaned.length === 0 ? null : cleaned;
};

const collapseIfEmptyObject = (obj: AnyObj): AnyObj | null => {
  const keys = Object.keys(obj);
  return keys.every((k) => isEmptyValue(obj[k])) ? null : obj;
};

const collapsibleKeys = new Set([
  "academicQualifications",
  "countriesWorkedIn",
  "militaryService",
  "previousOccupations",
  "relatives",
  "residency",
  "sponsorData",
  "vehicleDetails",
  "visitedCountries",
]);

const cleanValue = (val: unknown, keyPath: string[] = []): unknown => {
  if (isDateValue(val)) return val;

  if (Array.isArray(val)) return cleanArray(val);

  if (isPlainObject(val)) {
    const result: AnyObj = {};
    for (const [k, v] of Object.entries(val)) {
      result[k] = cleanValue(v, [...keyPath, k]);
    }

    const collapsed = collapseIfEmptyObject(result);
    if (collapsed === null) return null;

    const currentKey = keyPath[keyPath.length - 1];
    if (currentKey && collapsibleKeys.has(currentKey)) {
      const allEmpty = Object.values(result).every((v) => isEmptyValue(v));
      /* istanbul ignore next */
      if (allEmpty) return null;
    }

    return result;
  }

  return val;
};

export function cleanSecurityClearancePayload<T extends object>(payload: T): T {
  const working: AnyObj = structuredClone(payload) as AnyObj;

  for (const [k, v] of Object.entries(working)) {
    working[k] = cleanValue(v, [k]);
  }

  return working as T;
}

const vesselCollapsibleKeys = new Set<string>(["natureOfCall"]);

const cleanArrayVessel = (arr: unknown[], keyPath: string[]): unknown => {
  const cleaned = arr
    .map((item) => cleanValueVessel(item, keyPath))
    .filter((item) => !isEmptyValue(item));

  return cleaned.length === 0 ? null : cleaned;
};

const cleanValueVessel = (val: unknown, keyPath: string[]): unknown => {
  if (isDateValue(val)) return val;

  if (Array.isArray(val)) return cleanArrayVessel(val, keyPath);

  if (isPlainObject(val)) {
    const result: AnyObj = {};
    for (const [k, v] of Object.entries(val)) {
      result[k] = cleanValueVessel(v, [...keyPath, k]);
    }

    const collapsed = collapseIfEmptyObject(result);
    if (collapsed === null) return null;

    const currentKey = keyPath[keyPath.length - 1];
    if (currentKey && vesselCollapsibleKeys.has(currentKey)) {
      const allEmpty = Object.values(result).every((v) => isEmptyValue(v));
      /* istanbul ignore next */
      if (allEmpty) return null;
    }
    return result;
  }
  return isEmptyValue(val) ? null : val;
};

export function cleanVesselCallPayload<T extends object>(payload: T): T {
  const working: AnyObj = structuredClone(payload) as AnyObj;
  for (const [k, v] of Object.entries(working)) {
    working[k] = cleanValueVessel(v, [k]);
  }
  return working as T;
}

const pruneNullKeys = (val: unknown): unknown => {
  if (Array.isArray(val)) {
    return val.map((item) => pruneNullKeys(item));
  }

  if (isPlainObject(val)) {
    return Object.entries(val).reduce<AnyObj>((acc, [key, value]) => {
      if (value === null) {
        return acc;
      }
      acc[key] = pruneNullKeys(value);
      return acc;
    }, {});
  }

  return val;
};

export function removeNullValueKeys<T>(payload: T): T {
  const working = structuredClone(payload);
  return pruneNullKeys(working) as T;
}
